import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import Layout from '../components/project/Layout'
import TaskCard from '../components/kanban/TaskCard'
import WorkspaceHeaderConsolidated from '../components/workspace/WorkspaceHeaderConsolidated'
import { useAuthStore } from '../stores/auth-store'
import { useUIStore } from '../stores/ui-store'
import { trpc } from '../lib/trpc'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { ScrollArea } from '../components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Progress } from '../components/ui/progress'
import { Input } from '../components/ui/input'
import CompactTaskModal from '../components/kanban/CompactTaskModal'
import { 
  Clock,
  AlertCircle,
  CheckCircle,
  ArrowUpRight,
  History,
  ListChecks,
  Timer,
  Flame,
  TrendingUp,
  Calendar,
  Users,
  Loader2,
  ChevronRight,
  Target,
  Command,
  Search,
  Plus,
  Zap,
  BarChart3,
  Activity,
  Award,
  Brain
} from 'lucide-react'
import { cn } from '../lib/utils'
import { formatDistanceToNow } from 'date-fns'
import { Task } from '../types'

export default function Home() {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const utils = trpc.useUtils()
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [commandMode, setCommandMode] = useState(false)
  const [quickCommand, setQuickCommand] = useState('')
  const [focusMode, setFocusMode] = useState(false)
  
  const { 
    showTaskCreationModal, 
    taskCreationColumnId,
    taskCreationKanbanId,
    openTaskCreationModal, 
    closeTaskCreationModal
  } = useUIStore()

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, navigate])
  
  const { data: workspaces = [], isLoading: workspacesLoading } = trpc.workspace.list.useQuery()
  const activeWorkspace = workspaces[0]

  // Get all tasks with priority/deadline info
  const { data: allTasks = [], isLoading: tasksLoading } = trpc.task.list.useQuery(
    { workspaceId: activeWorkspace?.id || '' },
    { enabled: !!activeWorkspace?.id }
  )
  
  // Get recent activity
  const { data: recentActivity = [] } = trpc.activity.list.useQuery(
    { workspaceId: activeWorkspace?.id || '', limit: 20 },
    { enabled: !!activeWorkspace?.id }
  )
  
  // Get kanbans for task creation
  const { data: kanbans = [] } = trpc.kanban.list.useQuery(
    { workspaceId: activeWorkspace?.id || '' },
    { enabled: !!activeWorkspace?.id }
  )
  
  const firstKanban = kanbans[0]
  const { data: firstKanbanData } = trpc.kanban.get.useQuery(
    { id: firstKanban?.id || '' },
    { enabled: !!firstKanban?.id }
  )

  // Filter tasks by priority and deadlines
  const criticalTasks = allTasks.filter(t => t.priority === 'CRITICAL' && t.status !== 'DONE')
  const highPriorityTasks = allTasks.filter(t => t.priority === 'HIGH' && t.status !== 'DONE')
  const overdueTasks = allTasks.filter(t => {
    if (!t.dueDate || t.status === 'DONE') return false
    return new Date(t.dueDate) < new Date()
  })
  const dueSoonTasks = allTasks.filter(t => {
    if (!t.dueDate || t.status === 'DONE') return false
    const dueDate = new Date(t.dueDate)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    const dayAfter = new Date(today)
    dayAfter.setDate(dayAfter.getDate() + 2)
    return dueDate >= today && dueDate <= dayAfter
  })
  
  const inProgressTasks = allTasks.filter(t => t.status === 'IN_PROGRESS')
  const blockedTasks = allTasks.filter(t => t.status === 'BLOCKED')
  const completedToday = allTasks.filter(t => {
    if (t.status !== 'DONE') return false
    const completedDate = new Date(t.updatedAt)
    const today = new Date()
    return completedDate.toDateString() === today.toDateString()
  })

  // Calculate productivity metrics
  const totalTasks = allTasks.length
  const completedTasks = allTasks.filter(t => t.status === 'DONE').length
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0
  
  // Quick stats
  const stats = useMemo(() => ({
    critical: criticalTasks.length,
    overdue: overdueTasks.length,
    dueSoon: dueSoonTasks.length,
    inProgress: inProgressTasks.length,
    blocked: blockedTasks.length,
    completedToday: completedToday.length,
    completionRate
  }), [criticalTasks, overdueTasks, dueSoonTasks, inProgressTasks, blockedTasks, completedToday, completionRate])

  // Command mode handlers
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.metaKey && e.key === 'k') {
      e.preventDefault()
      setCommandMode(true)
    }
    if (e.key === 'Escape') {
      setCommandMode(false)
      setQuickCommand('')
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const executeQuickCommand = useCallback(() => {
    const cmd = quickCommand.toLowerCase().trim()
    
    if (cmd.startsWith('create task') || cmd.startsWith('new task')) {
      if (firstKanbanData?.columns?.[0]) {
        openTaskCreationModal(firstKanbanData.columns[0].id, firstKanban.id)
      }
    } else if (cmd === 'focus' || cmd === 'focus mode') {
      setFocusMode(true)
    } else if (cmd === 'board' || cmd === 'kanban') {
      navigate(`/board/${firstKanban?.id}`)
    } else if (cmd === 'calendar') {
      navigate('/calendar')
    } else if (cmd === 'notes') {
      navigate('/notes')
    } else if (cmd === 'chat') {
      navigate('/chat')
    }
    
    setCommandMode(false)
    setQuickCommand('')
  }, [quickCommand, firstKanbanData, firstKanban, navigate, openTaskCreationModal])

  const handleTaskClick = useCallback((taskId: string) => {
    setSelectedTaskId(taskId)
  }, [])

  const handleTaskClose = useCallback(() => {
    setSelectedTaskId(null)
  }, [])

  // Loading state
  if (!isAuthenticated || workspacesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading your workspace...</p>
        </div>
      </div>
    )
  }

  if (!activeWorkspace) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Workspace Found</h2>
          <p className="text-gray-600">Create a workspace to get started</p>
        </div>
      </div>
    )
  }

  return (
    <Layout>
      <div className={cn("flex flex-col h-screen", focusMode && "bg-gray-50")}>
        {/* Header */}
        <WorkspaceHeaderConsolidated />
        
        {/* Command Mode Overlay */}
        {commandMode && (
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-start justify-center pt-32">
            <div className="bg-white rounded-lg shadow-xl border p-4 w-96 max-w-md">
              <div className="flex items-center gap-3 mb-4">
                <Command className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-500">Quick Command</span>
              </div>
              <Input
                value={quickCommand}
                onChange={(e) => setQuickCommand(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && executeQuickCommand()}
                placeholder="Type a command... (create task, focus, board, calendar)"
                className="w-full"
                autoFocus
              />
              <div className="mt-3 text-xs text-gray-400">
                Try: "create task", "focus mode", "board", "calendar", "notes"
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full p-6">
            {/* Top Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              {/* Critical Tasks */}
              <Card className={cn("border-l-4", stats.critical > 0 ? "border-l-red-500 bg-red-50" : "border-l-gray-200")}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Critical</p>
                      <p className="text-2xl font-bold text-red-600">{stats.critical}</p>
                    </div>
                    <Flame className="w-8 h-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>

              {/* Overdue Tasks */}
              <Card className={cn("border-l-4", stats.overdue > 0 ? "border-l-orange-500 bg-orange-50" : "border-l-gray-200")}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Overdue</p>
                      <p className="text-2xl font-bold text-orange-600">{stats.overdue}</p>
                    </div>
                    <AlertCircle className="w-8 h-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>

              {/* Due Soon */}
              <Card className={cn("border-l-4", stats.dueSoon > 0 ? "border-l-yellow-500 bg-yellow-50" : "border-l-gray-200")}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Due Soon</p>
                      <p className="text-2xl font-bold text-yellow-600">{stats.dueSoon}</p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>

              {/* In Progress */}
              <Card className={cn("border-l-4", stats.inProgress > 0 ? "border-l-blue-500 bg-blue-50" : "border-l-gray-200")}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">In Progress</p>
                      <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
                    </div>
                    <Timer className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              {/* Completed Today */}
              <Card className={cn("border-l-4", stats.completedToday > 0 ? "border-l-green-500 bg-green-50" : "border-l-gray-200")}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Done Today</p>
                      <p className="text-2xl font-bold text-green-600">{stats.completedToday}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              {/* Completion Rate */}
              <Card className="border-l-4 border-l-purple-500 bg-purple-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Completion</p>
                      <p className="text-2xl font-bold text-purple-600">{stats.completionRate}%</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="mb-6">
              <div className="flex items-center gap-4">
                <Button 
                  onClick={() => {
                    if (firstKanbanData?.columns?.[0]) {
                      openTaskCreationModal(firstKanbanData.columns[0].id, firstKanban.id)
                    }
                  }}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  New Task
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/board/${firstKanban?.id}`)}
                  className="flex items-center gap-2"
                >
                  <Target className="w-4 h-4" />
                  View Board
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setCommandMode(true)}
                  className="flex items-center gap-2"
                >
                  <Command className="w-4 h-4" />
                  Quick Command
                  <Badge variant="secondary" className="ml-2">⌘K</Badge>
                </Button>
                {!focusMode ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setFocusMode(true)}
                    className="flex items-center gap-2"
                  >
                    <Zap className="w-4 h-4" />
                    Focus Mode
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => setFocusMode(false)}
                    className="flex items-center gap-2"
                  >
                    <ArrowUpRight className="w-4 h-4" />
                    Exit Focus
                  </Button>
                )}
              </div>
            </div>

            {/* Main Dashboard Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="tasks">Tasks</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Priority Tasks */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Flame className="w-5 h-5 text-red-500" />
                        High Priority Tasks
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-64">
                        <div className="space-y-2">
                          {[...criticalTasks, ...highPriorityTasks].slice(0, 6).map((task) => (
                            <div 
                              key={task.id} 
                              className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => handleTaskClick(task.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-sm">{task.title}</h4>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge 
                                      variant={task.priority === 'CRITICAL' ? 'destructive' : 'secondary'}
                                      className="text-xs"
                                    >
                                      {task.priority}
                                    </Badge>
                                    {task.dueDate && (
                                      <span className="text-xs text-gray-500 flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {formatDistanceToNow(new Date(task.dueDate), { addSuffix: true })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                          ))}
                          {[...criticalTasks, ...highPriorityTasks].length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                              <p>No high priority tasks! Great job!</p>
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {/* Recent Activity */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-blue-500" />
                        Recent Activity
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-64">
                        <div className="space-y-3">
                          {recentActivity.slice(0, 8).map((activity) => (
                            <div key={activity.id} className="flex items-start gap-3">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-900">{activity.action}</p>
                                <p className="text-xs text-gray-500">
                                  {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                                </p>
                              </div>
                            </div>
                          ))}
                          {recentActivity.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                              <History className="w-8 h-8 mx-auto mb-2" />
                              <p>No recent activity</p>
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>

                {/* Progress Section */}
                <div className="mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-purple-500" />
                        Workspace Progress
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span>Overall Completion</span>
                            <span className="font-medium">{completionRate}%</span>
                          </div>
                          <Progress value={completionRate} className="h-2" />
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-gray-900">{totalTasks}</p>
                            <p className="text-sm text-gray-500">Total Tasks</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-green-600">{completedTasks}</p>
                            <p className="text-sm text-gray-500">Completed</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
                            <p className="text-sm text-gray-500">In Progress</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-orange-600">{stats.overdue}</p>
                            <p className="text-sm text-gray-500">Overdue</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Tasks Tab */}
              <TabsContent value="tasks" className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Overdue Tasks */}
                  {overdueTasks.length > 0 && (
                    <Card className="border-orange-200">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-orange-700">
                          <AlertCircle className="w-5 h-5" />
                          Overdue ({overdueTasks.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-48">
                          <div className="space-y-2">
                            {overdueTasks.map((task) => (
                              <TaskCard
                                key={task.id}
                                task={task}
                                onClick={() => handleTaskClick(task.id)}
                                compact
                              />
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}

                  {/* Due Soon */}
                  {dueSoonTasks.length > 0 && (
                    <Card className="border-yellow-200">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-yellow-700">
                          <Clock className="w-5 h-5" />
                          Due Soon ({dueSoonTasks.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-48">
                          <div className="space-y-2">
                            {dueSoonTasks.map((task) => (
                              <TaskCard
                                key={task.id}
                                task={task}
                                onClick={() => handleTaskClick(task.id)}
                                compact
                              />
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}

                  {/* In Progress */}
                  {inProgressTasks.length > 0 && (
                    <Card className="border-blue-200">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-blue-700">
                          <Timer className="w-5 h-5" />
                          In Progress ({inProgressTasks.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-48">
                          <div className="space-y-2">
                            {inProgressTasks.map((task) => (
                              <TaskCard
                                key={task.id}
                                task={task}
                                onClick={() => handleTaskClick(task.id)}
                                compact
                              />
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}

                  {/* Blocked Tasks */}
                  {blockedTasks.length > 0 && (
                    <Card className="border-red-200">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-red-700">
                          <AlertCircle className="w-5 h-5" />
                          Blocked ({blockedTasks.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-48">
                          <div className="space-y-2">
                            {blockedTasks.map((task) => (
                              <TaskCard
                                key={task.id}
                                task={task}
                                onClick={() => handleTaskClick(task.id)}
                                compact
                              />
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}

                  {/* Completed Today */}
                  {completedToday.length > 0 && (
                    <Card className="border-green-200">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-5 h-5" />
                          Completed Today ({completedToday.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-48">
                          <div className="space-y-2">
                            {completedToday.map((task) => (
                              <TaskCard
                                key={task.id}
                                task={task}
                                onClick={() => handleTaskClick(task.id)}
                                compact
                              />
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* Activity Tab */}
              <TabsContent value="activity" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5 text-gray-500" />
                      Recent Workspace Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-96">
                      <div className="space-y-4">
                        {recentActivity.map((activity) => (
                          <div key={activity.id} className="flex items-start gap-4 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Activity className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 font-medium">{activity.action}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">
                                  {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                                </span>
                                {activity.entityType && (
                                  <Badge variant="outline" className="text-xs">
                                    {activity.entityType}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {recentActivity.length === 0 && (
                          <div className="text-center py-12 text-gray-500">
                            <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <h3 className="font-medium mb-2">No Recent Activity</h3>
                            <p className="text-sm">Activity will appear here as you work with tasks and projects</p>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Task Details Modal */}
      {selectedTaskId && (
        <CompactTaskModal
          taskId={selectedTaskId}
          onClose={handleTaskClose}
        />
      )}

      {/* Task Creation Modal */}
      {showTaskCreationModal && (
        <CompactTaskModal
          columnId={taskCreationColumnId}
          kanbanId={taskCreationKanbanId}
          onClose={closeTaskCreationModal}
        />
      )}
    </Layout>
  )
}